import { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSpring, animated } from 'react-spring';
import { useHistory } from 'react-router-dom';
import { fixedEncodeURIComponent, getLetterGradeFromNumberGrade } from '../../GlobalFunctions';
import { setUnit } from '../../Session';
import { showRightArrowWhiteIcon } from '../../core/core';
import { useWidth } from '../../CustomHooks';
import { selectClassroom } from '../../Classrooms';
import { CoreStandard } from '../../core/context/contextHelpers';
import { RootState } from '../../ApplicationState';
import styles from './StandardsByUnit.module.scss';

interface Props {
  title: string;
  id: string;
  completeStandards: string[];
  incompleteStandards: string[];
  barColor: string;
  isShouldAnimate: boolean;
  standardsLibrary: { [key: string]: CoreStandard };
}

const StandardsUnitRow = ({
  title,
  completeStandards,
  incompleteStandards,
  barColor,
  isShouldAnimate,
  id,
  standardsLibrary,
}: Props): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const barWidth = useWidth(ref);
  const history = useHistory();
  const dispatch = useDispatch();
  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });

  const complete = completeStandards.length;
  const total = completeStandards.length + incompleteStandards.length;
  const progressSpringProps = useSpring({
    from: { width: 0 },
    to: {
      width: barWidth !== null && barWidth !== 0 && isShouldAnimate ? barWidth * (complete / total) : 0,
    },
    delay: 200,
  });
  const goToUnit = () => {
    if (classroom.grade > 5) {
      history.push(`/lessonPlan/${id}/${classroom.language}/${classroom.grade}`);
      return;
    }
    dispatch(setUnit(id));
    history.push(
      `/${fixedEncodeURIComponent(classroom.className)}/lessonPlan/${id}-${getLetterGradeFromNumberGrade(
        classroom.grade,
      )}`,
    );
  };

  return (
    <div className={styles.barRow}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.incompleteBar} ref={ref}>
        <animated.div
          className={styles.completeBar}
          style={{
            backgroundColor: barColor,
            ...progressSpringProps,
            borderTopRightRadius: complete / total === 1 ? '4px' : 0,
            borderBottomRightRadius: complete / total === 1 ? '4px' : 0,
          }}
        >
          {barWidth != null && (barWidth * complete) / total > 30 && `${Math.floor((complete / total) * 100)}%`}
        </animated.div>
      </div>
      <div className={styles.standardsContainer}>
        <button className={styles.arrowLink} onClick={goToUnit}>
          {showRightArrowWhiteIcon()}
        </button>
        <ul className={styles.standards}>
          {completeStandards.map((standard) => (
            <li key={standard}>{standardsLibrary[standard].label}</li>
          ))}
        </ul>
        <ul className={styles.standards}>
          {incompleteStandards.map((standard) => (
            <li key={standard}>{standardsLibrary[standard].label}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default StandardsUnitRow;
